import React from "react";

import { FieldProps } from "../../../types/FormTypes";
import TextInput, { type TextInputProps } from "../TextInput/TextInput";
import Field from "../Field/Field";
import { useField } from "../Form/Form";

export interface TextFieldProps
  extends Omit<FieldProps<string>, "error">,
    Pick<TextInputProps, "size" | "autoComplete" | "clearable" | "onClear" | "clearAriaLabel"> {
  error?: boolean | string;
  type?: string;
  placeholder?: string;
  inputComponent?: React.ComponentType<any>;
  rows?: number;
}

const TextField = (props: TextFieldProps) => {
  const {
    className,
    name,
    label,
    helperText,
    required,
    disabled,
    fullWidth = false,
    type,
    placeholder,
    value: controlledValue,
    error,
    onChange,
    onValueChange,
    inputComponent: InputComponent,
    rows,
    ...otherProps
  } = props;
  const field = useField<string>({ name, value: controlledValue, onChange, onValueChange, required });
  const value = typeof field.value === "string" ? field.value : "";
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));

  const Input = InputComponent || TextInput;
  const inputProps = InputComponent && rows !== undefined ? { ...otherProps, rows } : { ...otherProps, type };

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label && <Field.Label required={field.required}>{label}</Field.Label>}
      <Field.Control>
        <Input
          ref={field.ref as any}
          name={name}
          value={value}
          onChange={field.onChange as any}
          onBlur={field.onBlur}
          disabled={disabled}
          fullWidth={fullWidth}
          placeholder={placeholder}
          error={hasError}
          {...inputProps}
        />
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
};

export default TextField;
